import React from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useAnsweringServiceDetails, useBreakpoint } from 'hooks';
import { AdminContext, AuthContext } from 'contexts';
import { Page, IconNameType, Tile, Modal } from 'components';
import { message, Spin } from '@bb/elements-web';
import { UserRoles } from '@bb/users';
import { UserController } from 'api';
import {
  checkUserBillingPermission,
  featureEnabled,
  makeModalResponsive,
} from 'utils';
import usesLegacyServiceProvider from 'utils/usesLegacyAnsweringServiceProvider';

type ButtonItemType = {
  icon: IconNameType;
  lines: [string, string];
  to?: string;
  href?: string;
  badgeCount?: number;
  onClick?: () => void;
};

const AdminPage: React.FC = () => {
  const { md } = useBreakpoint();
  const history = useHistory();
  const {
    auth,
    hasFeature,
    answeringPortalConfig,
    loadingAnsweringPortalConfig,
  } = React.useContext(AuthContext);
  const { ongoingTransferCount } = React.useContext(AdminContext);
  const [loadingInitial, setLoadingInitial] = React.useState(true);
  const [analyticsLink, setAnalyticsLink] = React.useState('');
  const [accountOwnerString, setAccountOwnerString] = React.useState('');
  const [usesLegacyOrProviderMissing, setUsesLegacyOrProviderMissing] =
    React.useState(false);

  const { hasActiveAnsweringService } = useAnsweringServiceDetails({
    viewingLevel: 'org',
    userId: auth.id,
  });

  /**
   * If subscriptionHubFeature FF is not enabled, then we want to always show the tiles per the existing rules
   * When FF is enabled, show when
   *   - The brokerage is subscribed to the appointment desk or live concierge service (paid for by anyone) OR
   *   - The Answering Service Provider is missing OR
   *   - The brokerage has a legacy answering service provider (even if there are no active subscriptions)
   */
  const showAnsweringServiceTile =
    !featureEnabled('subscriptionHubFeature') ||
    hasActiveAnsweringService === true ||
    usesLegacyOrProviderMissing;

  const billingTileOnClick = React.useCallback(() => {
    const showErrorModal = (content: string) => {
      makeModalResponsive(
        Modal.error({
          title: 'You are not authorized to manage billing',
          content,
          centered: true,
        }),
      );
    };

    if (auth.assumingUserId || auth.isAssumedNotBB) {
      showErrorModal('You cannot manage billing while assuming a user.');
    } else if (!checkUserBillingPermission(auth)) {
      showErrorModal(
        `You must ask the account owner${
          accountOwnerString ? ` ${accountOwnerString} ` : ' '
        }for permission.`,
      );
    } else {
      const billingPath = featureEnabled('subscriptionHubFeature')
        ? '/subscriptions_payments/org'
        : '/billing';

      history.push(billingPath);
    }
  }, [auth, accountOwnerString, history]);

  const answeringTileOnClick = React.useCallback(() => {
    const answeringServicePath = featureEnabled('subscriptionHubFeature')
      ? '/answering_service/org'
      : '/admin/appointment_desk';

    history.push(answeringServicePath);
  }, [history]);

  const canEditListing = featureEnabled('supraHideAdminFunctionalityFeature')
    ? (hasFeature('SUPRA_MVP') &&
        (auth.org.country === 'CAN' || auth.parentOrgType === 'mlsStaff')) ||
      !hasFeature('SUPRA_MVP')
    : true;

  const buttonList: ButtonItemType[] = (
    [
      {
        icon: 'antdFolderOutlined',
        lines: ['Draft', 'Listings'],
        to: '/listing/list/admin/0',
      },
      {
        icon: 'delete',
        lines: ['Deleted', 'Listings'],
        to: '/listing/list/deleted/0',
      },

      ...(canEditListing
        ? [
            {
              icon: 'plus',
              lines: ['Create', 'Listing'],
              to: '/listing/new',
            },
          ]
        : []),
      {
        icon: 'antdUserOutlined',
        lines: ['Manage', 'Users'],
        to: '/users/list',
        badgeCount: ongoingTransferCount,
      },

      ...(canEditListing
        ? [
            {
              icon: 'antdShrinkOutlined',
              lines: ['Merge / Combine', 'Listings'],
              to: '/listing/merge/options',
            },
          ]
        : []),
      {
        icon: 'bullhorn',
        lines: ['Send', 'Announcement'],
        to: '/announcement/list',
      },
      auth.roles[auth.role] >= auth.roles.BRANCH_ADMIN &&
        hasFeature('ANALYTICS') && {
          icon: 'chart',
          lines: ['Reporting', 'Analytics'],
          href: analyticsLink || '',
        },
      showAnsweringServiceTile &&
        auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN && {
          icon: 'antdPhoneOutlined',
          lines: featureEnabled('subscriptionHubFeature')
            ? ['Brokerage', 'Answering Service']
            : ['Live Concierge', 'Service'],
          onClick: answeringTileOnClick,
        },
      {
        icon: 'lock',
        lines: ['Manage', 'Lock Boxes'],
        to: '/lockboxes',
      },
      {
        icon: 'template',
        lines: ['Template', 'Manager'],
        to: '/template/manage',
      },
      {
        icon: 'settings',
        lines: ['Brokerage', 'Settings'],
        to: '/org/settings',
      },
      auth.roles[auth.role] > auth.roles.BRANCH_ADMIN && {
        icon: 'antdCreditCardOutlined',
        lines: featureEnabled('subscriptionHubFeature')
          ? ['Subscriptions', 'and Payments']
          : ['Brokerage', 'Billing & Invoices'],
        onClick: billingTileOnClick,
      },
    ] as ButtonItemType[]
  ).filter(Boolean);

  const getAnalyticsUrl = async () => {
    try {
      const {
        data: { analyticsUrl },
      } = await axios.get<{ analyticsUrl: string }>('/secrets/analyticsUrl');

      setAnalyticsLink(analyticsUrl || '');
    } catch (err) {
      message.error('Server error. Please try again later');
      history.replace('/');
    }
  };

  const getOrgOwnerString = async () => {
    try {
      const { users } = await UserController.org({ role: [UserRoles.Owner] });

      const owner = users.find(({ role }) => role === UserRoles.Owner);
      const { firstName, lastName, email } = owner ?? {};

      setAccountOwnerString(
        owner ? `(${firstName} ${lastName}, ${email})` : '',
      );
    } catch (err) {
      message.error('Server error. Please try again later');
      history.replace('/');
    }
  };

  const getUsesLegacyProvider = async () => {
    try {
      const result = await usesLegacyServiceProvider(
        answeringPortalConfig,
        auth.org,
      );
      setUsesLegacyOrProviderMissing(result);
    } catch (e) {
      message.error('Error checking answering service provider');
    }
  };

  React.useEffect(() => {
    const fetchInitialData = async () => {
      await Promise.all([
        getAnalyticsUrl(),
        getOrgOwnerString(),
        getUsesLegacyProvider(),
      ]);
      setLoadingInitial(false);
    };

    fetchInitialData();
  }, []);

  return (
    <Page type="hollow" title="Admin">
      {loadingAnsweringPortalConfig || loadingInitial ? (
        <StyledSpinContainer>
          <Spin size="large" dataCy={'admin-loading-progress'} />
        </StyledSpinContainer>
      ) : (
        <StyledGrid $isMobile={!md}>
          {buttonList.map(({ icon, lines, to, href, badgeCount, onClick }) => {
            const key = lines.join('-');
            return (
              <Tile
                key={key}
                icon={icon}
                lines={lines}
                to={to}
                href={href}
                badgeCount={badgeCount}
                dataCy={`admin-${key}`}
                {...(onClick ? { onClick, to: undefined } : {})}
              />
            );
          })}
        </StyledGrid>
      )}
    </Page>
  );
};

const StyledSpinContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 50px;
`;

const StyledGrid = styled.div<{ $isMobile: boolean }>`
  display: grid;
  gap: 35px;
  grid-auto-rows: 112px;
  grid-template-columns: repeat(auto-fit, 235px);
  ${({ $isMobile }) =>
    $isMobile &&
    `
  grid-template-columns: 1fr;
  `}
`;

export default AdminPage;
